import { ModuleSettingsStore } from "../contentstack/moduleSettingsStore.js";
import { ALL_HOURS_MATCH, MON_THROUGH_SAT_HOURS_MATCH, WEEKEND_HOURS_MATCH, NO_HOURS_MATCH, MODULE_SETTINGS_WAREHOUSES_PAGE_URL, DEFAULT_BASE_URL } from "./constants.js";
import { formatTime, convertTo12HourFormat } from "./warehouseHelpers.js";
const convert24hrTo12hr = (time) => {
  return convertTo12HourFormat(time);
};
const getHourRangeSeparator = (locale) => {
  if (locale === "fr-ca") {
    return " à ";
  } else {
    return " - ";
  }
};
const transformHoursData = (hours, locale = "en-us") => {
  if (!hours || hours.length === 0) {
    return [];
  }
  const formatTimeForLocale = (time) => {
    return locale === "fr-ca" ? time : convert24hrTo12hr(time);
  };
  switch (checkHoursOverlap(hours)) {
    case ALL_HOURS_MATCH:
      return combineAndMoveHoursLabels(
        hours[0],
        hours[2],
        void 0,
        locale
      );
    case MON_THROUGH_SAT_HOURS_MATCH:
      return combineAndMoveHoursLabels(
        hours[0],
        hours[1],
        hours[2],
        locale
      );
    case WEEKEND_HOURS_MATCH:
      return combineAndMoveHoursLabels(
        hours[1],
        hours[2],
        hours[0],
        locale
      );
    case NO_HOURS_MATCH:
      return hours.map((hoursInfo) => {
        return `${hoursInfo.title[0].value}: ${formatTimeForLocale(
          hoursInfo.open
        )} - ${formatTimeForLocale(hoursInfo.close)}`;
      });
  }
};
const getWeekdaysData = (hours) => {
  return hours.filter((hour) => hour.weekDays.length === 5);
};
const getSaturdayData = (hours) => {
  return hours.filter(
    (hour) => hour.weekDays.length === 1 && hour.weekDays[0] === 7
  );
};
const getSundayData = (hours) => {
  return hours.filter(
    (hour) => hour.weekDays.length === 1 && hour.weekDays[0] === 1
  );
};
const combineAndMoveHoursLabels = (beginningHoursData, endinghoursData, unModifiedHoursData, locale = "en-us") => {
  const formatTimeForLocale = (time) => {
    return locale === "fr-ca" ? time : convert24hrTo12hr(time);
  };
  const beginniningHoursTitleDelimited = beginningHoursData.title[0].value.split("-");
  const endingHoursTitleDelimited = endinghoursData.title[0].value.split("-");
  const combinedTitle = `${beginniningHoursTitleDelimited[0]}-${endingHoursTitleDelimited[0]}`;
  if (!unModifiedHoursData) {
    return [
      `${combinedTitle}: ${formatTimeForLocale(
        beginningHoursData.open
      )} - ${formatTimeForLocale(beginningHoursData.close)}`
    ];
  }
  if (unModifiedHoursData.weekDays.length > 1) {
    return [
      `${unModifiedHoursData.title[0].value}: ${formatTimeForLocale(
        unModifiedHoursData.open
      )} - ${formatTimeForLocale(unModifiedHoursData.close)}`,
      `${combinedTitle}: ${formatTimeForLocale(
        beginningHoursData.open
      )} - ${formatTimeForLocale(beginningHoursData.close)}`
    ];
  } else if (unModifiedHoursData.weekDays.length === 1) {
    return [
      `${combinedTitle}: ${formatTimeForLocale(
        beginningHoursData.open
      )} - ${formatTimeForLocale(beginningHoursData.close)}`,
      `${unModifiedHoursData.title[0].value}: ${formatTimeForLocale(
        unModifiedHoursData.open
      )} - ${formatTimeForLocale(unModifiedHoursData.close)}`
    ];
  }
};
const combineHoursLabel = (beginningHoursData, endingHoursData, locale = "en-us", closedLabel = "Closed") => {
  var _a, _b;
  const beginniningHoursTitleDelimited = beginningHoursData.title[0].value.split("-");
  const endingHoursTitleDelimited = endingHoursData.title[0].value.split("-");
  const combinedTitle = `${beginniningHoursTitleDelimited[0]}-${endingHoursTitleDelimited[0]}`;
  const separator = getHourRangeSeparator(locale);
  if (((_a = beginningHoursData.hoursType) == null ? void 0 : _a.code) === "close" && ((_b = endingHoursData.hoursType) == null ? void 0 : _b.code) === "close") {
    return {
      key: combinedTitle,
      label: `${combinedTitle}: ${closedLabel}`,
      title: combinedTitle,
      hours: closedLabel
    };
  } else {
    const hours = `${formatTime(beginningHoursData.open, locale)}${separator}${formatTime(beginningHoursData.close, locale)}`;
    return {
      key: combinedTitle,
      label: `${combinedTitle}: ${hours}`,
      title: combinedTitle,
      hours
    };
  }
};
const getHoursLabelKeyValue = (hoursData, locale = "en-us") => {
  const title = hoursData.title[0].value;
  const separator = getHourRangeSeparator(locale);
  const hours = `${formatTime(hoursData.open, locale)}${separator}${formatTime(hoursData.close, locale)}`;
  const label = `${title}: ${hours}`;
  return {
    key: title,
    label,
    title,
    hours
  };
};
const checkHoursOverlap = (hours) => {
  if (hours.length !== 3) {
    return NO_HOURS_MATCH;
  }
  const monThruFri = getWeekdaysData(hours);
  const saturday = getSaturdayData(hours);
  const sunday = getSundayData(hours);
  if (monThruFri.length === 0 || saturday.length === 0 || sunday.length === 0) {
    return NO_HOURS_MATCH;
  }
  if (hours.every(
    (hour) => hour.open === monThruFri[0].open && hour.close === monThruFri[0].close
  )) {
    return ALL_HOURS_MATCH;
  } else if (monThruFri[0].open === saturday[0].open && monThruFri[0].close === saturday[0].close) {
    return MON_THROUGH_SAT_HOURS_MATCH;
  } else if (saturday[0].open === sunday[0].open && saturday[0].close === sunday[0].close) {
    return WEEKEND_HOURS_MATCH;
  }
  return NO_HOURS_MATCH;
};
const transformAddressData = (address) => {
  return [
    address.line1,
    `${address.city}, ${address.territory} ${address.postalCode}`
  ];
};
const getTitleKey = (labels) => {
  const titleKey = labels[0].split(":")[0];
  return titleKey;
};
const constructWarehouseDetailsLink = (warehouseId, address, moduleSettings) => {
  let baseUrl;
  if (moduleSettings) {
    baseUrl = new ModuleSettingsStore({
      composable_window: [],
      // unused in this moduleSettings
      key_value: moduleSettings.key_value
    }).getKeyValueItem(MODULE_SETTINGS_WAREHOUSES_PAGE_URL);
    baseUrl = baseUrl ? baseUrl : DEFAULT_BASE_URL;
  }
  const domain = window.location.origin;
  const city = address.city.toLowerCase();
  const territory = address.territory.toLowerCase();
  return `${domain}${baseUrl}${city}-${territory}-${warehouseId}.html`;
};
const getWarehouseTileLayout = (layout) => {
  if (!layout) return "";
  if (layout === "half") return "rich_text_half_grow";
  return `rich_text_${layout}`;
};
const getWarehouseHoursLabels = (warehouseHours, locale = "en-us", closedLabel = "Closed") => {
  if (!warehouseHours || warehouseHours.length === 0) {
    return [];
  }
  let hoursLabels = [];
  const hoursOverlap = checkHoursOverlap(warehouseHours);
  if (checkHoursOverlap(warehouseHours)) {
    const monThruFriHours = getWeekdaysData(warehouseHours);
    const satHours = getSaturdayData(warehouseHours);
    const sunHours = getSundayData(warehouseHours);
    if (hoursOverlap === ALL_HOURS_MATCH) {
      hoursLabels.push(
        combineHoursLabel(
          monThruFriHours[0],
          sunHours[0],
          locale,
          closedLabel
        )
      );
    } else if (hoursOverlap === MON_THROUGH_SAT_HOURS_MATCH) {
      hoursLabels.push(
        combineHoursLabel(
          monThruFriHours[0],
          satHours[0],
          locale,
          closedLabel
        )
      );
      hoursLabels.push(getHoursLabelKeyValue(sunHours[0], locale));
    } else if (hoursOverlap === WEEKEND_HOURS_MATCH) {
      hoursLabels.push(getHoursLabelKeyValue(monThruFriHours[0], locale));
      hoursLabels.push(
        combineHoursLabel(
          satHours[0],
          sunHours[0],
          locale,
          closedLabel
        )
      );
    } else if (hoursOverlap === "NO_HOURS_MATCH") {
      hoursLabels = (warehouseHours == null ? void 0 : warehouseHours.reduce(
        (hourLabels, hour) => {
          var _a, _b, _c;
          if (((_a = hour.hoursType) == null ? void 0 : _a.code) === "close") {
            const title = (_b = hour.title[0]) == null ? void 0 : _b.value;
            hourLabels.push({
              key: title,
              label: `${title}: ${closedLabel}`,
              title,
              hours: closedLabel
            });
          } else if (((_c = hour.title[0]) == null ? void 0 : _c.value) && hour.open && hour.close) {
            const separator = getHourRangeSeparator(locale);
            const title = hour.title[0].value;
            const hours = `${formatTime(hour.open, locale)}${separator}${formatTime(hour.close, locale)}`;
            hourLabels.push({
              key: title,
              label: `${title}: ${hours}`,
              title,
              hours
            });
          }
          return hourLabels;
        },
        []
      )) || [];
    }
  }
  return hoursLabels;
};
const getWarehouseHolidayHoursLabels = (warehouseHolidays, locale = "en-us", closedLabel = "Closed", openLabel = "Open") => {
  if (!warehouseHolidays || warehouseHolidays.length === 0) {
    return [];
  }
  const holidayHoursLabels = warehouseHolidays == null ? void 0 : warehouseHolidays.reduce(
    (hourLabels, holiday) => {
      var _a, _b, _c, _d;
      if ((_b = (_a = holiday == null ? void 0 : holiday.name) == null ? void 0 : _a[0]) == null ? void 0 : _b.value) {
        const holidayName = holiday.name[0].value;
        if (holiday.holidayHoursType.code === "closed") {
          hourLabels.push({
            key: holidayName,
            label: `${holidayName} ${closedLabel}`,
            title: holidayName,
            hours: closedLabel
          });
        }
        if (holiday.holidayHoursType.code === "open") {
          hourLabels.push({
            key: holidayName,
            label: `${holidayName} ${openLabel}`,
            title: holidayName,
            hours: openLabel
          });
        }
        if (((_c = holiday.hour) == null ? void 0 : _c.open) && ((_d = holiday.hour) == null ? void 0 : _d.close) && holiday.holidayHoursType.code === "hours-change") {
          const separator = getHourRangeSeparator(locale);
          const hours = `${formatTime(holiday.hour.open, locale)}${separator}${formatTime(holiday.hour.close, locale)}`;
          hourLabels.push({
            key: holidayName,
            label: `${holidayName} ${hours}`,
            title: holidayName,
            hours
          });
        }
      }
      return hourLabels;
    },
    []
  );
  return holidayHoursLabels;
};
export {
  checkHoursOverlap,
  combineAndMoveHoursLabels,
  combineHoursLabel,
  constructWarehouseDetailsLink,
  getHourRangeSeparator,
  getHoursLabelKeyValue,
  getSaturdayData,
  getSundayData,
  getTitleKey,
  getWarehouseHolidayHoursLabels,
  getWarehouseHoursLabels,
  getWarehouseTileLayout,
  getWeekdaysData,
  transformAddressData,
  transformHoursData
};
